/**
 * @fileoverview Hotels.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 */
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import SEO from '../components/ui/SEO'
import { FadeUp, ScaleIn } from '../utils/motion'
import { apiFetch } from '../utils/api'
import DynamicImage from '../components/ui/DynamicImage'
import HotelProfileOverlay from '../components/hotels/HotelProfileOverlay'

function Hotels() {
    const { t } = useTranslation();
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedHotel, setSelectedHotel] = useState(null);

    useEffect(() => {
        apiFetch('/hotels/public')
            .then(res => setHotels(res || []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const schemaData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Luxury Hotels in Sri Lanka | KITH Travels",
        "description": "Explore premium, handpicked accommodations in Sri Lanka selected by KITH Travels.",
    };

    return (
        <div className='w-full bg-gray-50 min-h-screen pb-20'>
            <SEO 
                title="Luxury Accommodations" 
                schema={schemaData}
                description="Discover our exclusive selection of 3-star, 4-star, 5-star, and Luxury Resorts in Sri Lanka, handpicked by KITH Travels for your perfect getaway."
                keywords="Sri Lanka hotels, luxury resorts Sri Lanka, KITH Travels accommodation, best hotels Sri Lanka"
            />
            <div className='w-full px-[5%] md:px-[10%] pt-32 pb-12 text-center flex flex-col gap-4'>
                <FadeUp>
                    <span className='text-KITH-primary font-bold tracking-widest uppercase text-xs mb-2 block'>{t('hotels.premium_stays')}</span>
                    <h1 className='text-3xl md:text-5xl font-bold text-gray-800 leading-tight'>
                        {t('hotels.partner_accommodations')}
                    </h1>
                    <p className='text-gray-500 text-sm max-w-2xl mx-auto mt-4'>
                        {t('hotels.partner_desc')}
                    </p>
                </FadeUp>
            </div>
            
            <div className='w-full px-[5%] md:px-[10%]'>
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="w-full bg-white rounded-3xl shadow-sm border border-gray-100 p-4 h-80 animate-pulse"></div>
                        ))}
                    </div>
                ) : hotels.length === 0 ? (
                    <div className='text-center py-20 text-gray-400 font-medium'>{t('hotels.no_hotels')}</div>
                ) : (
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                        {hotels.map((hotel, index) => (
                            <ScaleIn key={hotel._id || index} delay={index * 0.1}>
                                <div 
                                    onClick={() => setSelectedHotel(hotel)}
                                    className='bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 transition-all cursor-pointer group flex flex-col'
                                >
                                    <div className='w-full aspect-[4/3] overflow-hidden relative'>
                                        {hotel.images && hotel.images.length > 0 ? (
                                            <DynamicImage 
                                                image={hotel.images[0]} 
                                                alt={hotel.name} 
                                                className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-500' 
                                            />
                                        ) : (
                                            <div className='w-full h-full bg-gray-200 flex items-center justify-center text-gray-400'>
                                                <i className='fi fi-br-picture text-3xl'></i>
                                            </div>
                                        )}
                                        {hotel.classification && (
                                            <div className='absolute top-4 right-4 bg-white/90 backdrop-blur text-gray-800 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm'>
                                                {hotel.classification}
                                            </div>
                                        )}
                                    </div>
                                    <div className='p-6 flex flex-col gap-2 text-left'>
                                        <h3 className='text-xl font-bold text-gray-800 text-left'>{hotel.name}</h3>
                                        <div className='flex items-center gap-2 text-gray-500 text-sm text-left'>
                                            <i className='fi fi-br-map-marker text-KITH-primary'></i>
                                            <span>
                                                {hotel.city && hotel.location ? `${hotel.city}, ${hotel.location}` : (hotel.city || hotel.location || t('hotels.unknown_location'))}
                                            </span>
                                        </div>
                                        {hotel.description && (
                                            <p className='text-gray-600 text-sm mt-2 line-clamp-3 leading-relaxed text-justify'>
                                                {hotel.description}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </ScaleIn>
                        ))}
                    </div>
                )}
            </div>

            <HotelProfileOverlay 
                isOpen={!!selectedHotel} 
                onClose={() => setSelectedHotel(null)} 
                hotel={selectedHotel} 
            />
        </div>
    )
}

export default Hotels
